/**
 * Custom checkbox/radiogroup renderers that keep the "Other" choice item and its
 * free-text comment input together inside a single wrapper element.
 *
 * Why this exists:
 * SurveyJS's default renderers (SurveyQuestionCheckbox / SurveyQuestionRadiogroup) render
 * the "Other" choice item as part of the regular item list, and then render its comment
 * textbox (renderOther()) as a SEPARATE sibling appended after all the items/footer. In a
 * single-column layout that's fine because everything stacks vertically anyway. But in the
 * 2-column layout (.two-layout-col, see resetSurvey.scss) each direct child of the items
 * container becomes its own CSS grid cell — so the "Other" item lands in one cell and its
 * comment box lands in the NEXT cell, breaking the visual pairing.
 *
 * Post-render DOM patching (moving nodes around in onAfterRenderQuestion) does not survive
 * SurveyJS/React's own re-renders, so instead these renderers own the markup directly:
 * they render the normal items, but when the "Other" item is present they render it and its
 * comment box together inside one <div className="sd-other-group">. Because this is part of
 * the real render output, it persists across re-renders like any other React content.
 *
 * These are registered as the DEFAULT renderer for "checkbox" and "radiogroup" (not an
 * opt-in renderAs mode), since every "Other" option across the app should keep this pairing.
 */
import React from "react";
import { RendererFactory } from "survey-core";
import { SurveyQuestionCheckbox, SurveyQuestionRadiogroup, ReactQuestionFactory } from "survey-react-ui";

// Builds the final list of body elements from { element, item } pairs. The "Other" item
// (identified by otherItemValue) always stays in its original position in the list — it is
// never dropped. It is ALWAYS wrapped in a single .sd-other-group div (whether or not it is
// selected) so the 2-column layout CSS (.two-layout-col ... > .sd-other-group { column-span:
// all }) can pull the whole "Other" block full-width beneath the two columns in both states.
// When the "Other" choice is currently selected, the free-text comment box (renderOtherFn())
// is rendered inside the same wrapper, directly below the item.
function buildBodyElements(renderedPairs, otherItemValue, isOtherSelected, renderOtherFn) {
    return renderedPairs.map((pair) => {
        const isOtherItem = otherItemValue !== undefined && pair.item && pair.item.value === otherItemValue;
        if (isOtherItem) {
            return (
                React.createElement("div", { className: "sd-other-group", key: "other-group-" + pair.item.value },
                    pair.element,
                    isOtherSelected ? renderOtherFn() : null
                )
            );
        }
        return pair.element;
    });
}

class CheckboxWithOtherGroup extends SurveyQuestionCheckbox {
    // Mirrors the base getItems(), but returns { element, item } pairs instead of just
    // elements so the "Other" item can be identified and pulled out afterwards.
    getItemPairs(cssClasses, choices) {
        const pairs = [];
        for (let i = 0; i < choices.length; i++) {
            const item = choices[i];
            const renderedItem = this.renderItem(item, i === 0, cssClasses, "" + i);
            if (renderedItem) {
                pairs.push({ element: renderedItem, item });
            }
        }
        return pairs;
    }

    getBody(cssClasses) {
        const choices = this.question.blockedRow ? this.question.dataChoices : this.question.bodyItems;
        const pairs = this.getItemPairs(cssClasses, choices);
        const otherItemValue = this.question.hasOther ? this.question.otherItem?.value : undefined;
        const elements = buildBodyElements(pairs, otherItemValue, this.question.isOtherSelected, () => this.renderOther());

        return this.question.blockedRow
            ? React.createElement("div", { className: cssClasses.rootRow }, elements)
            : React.createElement(React.Fragment, null, elements);
    }

    renderElement() {
        const cssClasses = this.question.cssClasses;
        return (
            React.createElement("fieldset", {
                className: this.question.getSelectBaseRootCss(),
                ref: (fieldset) => (this.setControl(fieldset)),
                role: this.question.a11y_input_ariaRole,
                "aria-required": this.question.a11y_input_ariaRequired,
                "aria-label": this.question.a11y_input_ariaLabel,
                "aria-labelledby": this.question.a11y_input_ariaLabelledBy,
                "aria-describedby": this.question.a11y_input_ariaDescribedBy,
                "aria-invalid": this.question.a11y_input_ariaInvalid,
                "aria-errormessage": this.question.a11y_input_ariaErrormessage
            },
                React.createElement("legend", { className: "sv-hidden" }, this.question.locTitle.renderedHtml),
                this.getHeader(),
                this.question.hasColumns ? this.getColumnedBody(cssClasses) : this.getBody(cssClasses),
                this.getFooter()
                // Note: renderOther() is intentionally NOT appended here — getBody() already
                // renders it inline, paired with the "Other" item, when applicable.
            )
        );
    }
}

class RadiogroupWithOtherGroup extends SurveyQuestionRadiogroup {
    getItemPairs(cssClasses, choices, value) {
        const pairs = [];
        for (let i = 0; i < choices.length; i++) {
            const item = choices[i];
            const renderedItem = this.renderItem(item, value, cssClasses, "" + i);
            if (renderedItem) {
                pairs.push({ element: renderedItem, item });
            }
        }
        return pairs;
    }

    getBody(cssClasses) {
        const choices = this.question.blockedRow ? this.question.dataChoices : this.question.bodyItems;
        const value = this.getStateValue();
        const pairs = this.getItemPairs(cssClasses, choices, value);
        const otherItemValue = this.question.hasOther ? this.question.otherItem?.value : undefined;
        const elements = buildBodyElements(pairs, otherItemValue, this.question.isOtherSelected, () => this.renderOther(cssClasses));

        return this.question.blockedRow
            ? React.createElement("div", { className: cssClasses.rootRow }, elements)
            : React.createElement(React.Fragment, null, elements);
    }

    renderElement() {
        const cssClasses = this.question.cssClasses;
        let clearButton = null;
        if (this.question.showClearButtonInContent) {
            clearButton = (
                React.createElement("div", null,
                    React.createElement("input", {
                        type: "button",
                        className: this.question.cssClasses.clearButton,
                        onClick: () => this.question.clearValue(true),
                        value: this.question.clearButtonCaption
                    })
                )
            );
        }
        return (
            React.createElement("fieldset", {
                className: this.question.getSelectBaseRootCss(),
                ref: (fieldset) => (this.setControl(fieldset)),
                role: this.question.a11y_input_ariaRole,
                "aria-required": this.question.a11y_input_ariaRequired,
                "aria-label": this.question.a11y_input_ariaLabel,
                "aria-labelledby": this.question.a11y_input_ariaLabelledBy,
                "aria-describedby": this.question.a11y_input_ariaDescribedBy,
                "aria-invalid": this.question.a11y_input_ariaInvalid,
                "aria-errormessage": this.question.a11y_input_ariaErrormessage
            },
                this.question.hasColumns ? this.getColumnedBody(cssClasses) : this.getBody(cssClasses),
                this.getFooter(),
                // renderOther() is rendered inline via getBody() when applicable — not here.
                clearButton
            )
        );
    }
}

// Register as the DEFAULT renderer (useAsDefault=true) so EVERY checkbox/radiogroup
// question gets this "Other" pairing behaviour automatically, with no per-question
// opt-in required. A question that explicitly sets renderAs to something else (e.g.
// the existing "toggle-switch" checkbox renderer) is unaffected, since
// RendererFactory.getRenderer() only falls back to the type's default when the
// question's own renderAs is empty.
ReactQuestionFactory.Instance.registerQuestion("sv-checkbox-other-group", (props) => {
    return React.createElement(CheckboxWithOtherGroup, props);
});
ReactQuestionFactory.Instance.registerQuestion("sv-radiogroup-other-group", (props) => {
    return React.createElement(RadiogroupWithOtherGroup, props);
});

RendererFactory.Instance.registerRenderer("checkbox", "other-group", "sv-checkbox-other-group", true);
RendererFactory.Instance.registerRenderer("radiogroup", "other-group", "sv-radiogroup-other-group", true);

export { CheckboxWithOtherGroup, RadiogroupWithOtherGroup };
